import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import { FEATURE_PERMISSIONS, canViewAllData } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { Link } from 'react-router-dom';
import Toast from '../components/common/Toast';
import PaginationControls from '../components/common/PaginationControls';
import Tooltip from '../components/common/Tooltip';
import { exportData as exportDataUtil } from '../utils/exportUtils';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import { formatDate } from '../utils/dateHelpers';
import { currentInactivityRows } from '../utils/reportOwnership';

/**
 * Customer Inactivity Report
 * 
 * Identifies active customers (isActive = 1) with no orders within the past 365 days
 * 
 * Caching Strategy:
 * - Cache ID: 'inactivity_365d'
 * - TTL: 24 hours (daily refresh sufficient for yearly lookback)
 * - Cache should be generated by Cloud Function (generateInactivityCache)
 * - Client only reads cache, admins can trigger refresh via callable function
 */

const CACHE_ID = 'inactivity_365d';
const CACHE_TTL_HOURS = 24;
const PAGE_SIZE = 25;

const CustomerInactivityReportPage = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [cachedCustomers, setInactiveCustomers] = useState([]);
    const [currentCustomers, setCurrentCustomers] = useState([]);
    const inactiveCustomers = useMemo(() => currentInactivityRows(
        cachedCustomers, currentCustomers, user,
        canViewAllData(user) || can(FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS) || can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL)
    ), [cachedCustomers, currentCustomers, user, can]);
    const [usingCache, setUsingCache] = useState(false);
    const [cacheAge, setCacheAge] = useState(null);
    const [cacheAvailable, setCacheAvailable] = useState(true);
    const fb = useFilterBar({ search: '', selectedRep: '', selectedStatus: '', selectedStates: [] });
    const [sortField, setSortField] = useUrlState('sortField', 'lastOrderDate');
    const [sortDirection, setSortDirection] = useUrlState('sortDirection', 'desc');
    const [currentPage, setCurrentPage] = useUrlState('currentPage', 1);
    const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
    const stateDropdownRef = useRef(null);
    const [refreshing, setRefreshing] = useState(false);
    const [generatingCache, setGeneratingCache] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(0);
    const [lastCacheUpdate, setLastCacheUpdate] = useState(null);
    const [cacheRefreshCooldown, setCacheRefreshCooldown] = useState(12); // Default 12 hours
    const refreshTimerRef = useRef(null);

    useEffect(() => {
        if (!user?.uid) return undefined;
        setCurrentCustomers([]);
        return onSnapshot(collection(db, collectionPath('customers')), snapshot => {
            setCurrentCustomers(snapshot.docs.map(d => ({ ...d.data(), id: d.id })));
        }, error => {
            console.error('[InactivityReport] Current ownership lookup failed:', error);
            setCurrentCustomers([]);
            setToast({ message: `Could not load current customer assignments: ${error.message}`, type: 'error' });
        });
    }, [user?.uid]);

    // Close state dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target)) {
                setStateDropdownOpen(false);
            }
        };
        if (stateDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [stateDropdownOpen]);

    // Fetch cache refresh cooldown from system settings
    useEffect(() => {
        const fetchCooldown = async () => {
            try {
                const settingDoc = await getDoc(doc(db, collectionPath('systemSettings'), 'cacheRefreshCooldownHours'));
                if (settingDoc.exists() && settingDoc.data().value) {
                    setCacheRefreshCooldown(Number(settingDoc.data().value));
                }
            } catch (error) {
                console.error('[CustomerInactivityReportPage] Error fetching cache cooldown setting:', error);
                // Keep default value of 12 hours
            }
        };
        fetchCooldown();
    }, []);

    // Cleanup progress timer on unmount
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, []);

    // Fetch data - cache only approach (computation done server-side)
    useEffect(() => {
        if (!user || !user.uid) return;
        if (userRole === null) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Try cache first - this should be pre-generated by Cloud Function
                const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), CACHE_ID));

                if (cacheDoc.exists()) {
                    const cacheData = cacheDoc.data();
                    const lastUpdated = cacheData.lastUpdated?.toDate?.() || new Date(0);
                    const ageHours = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);

                    if (cacheData.inactiveCustomers) {
                        console.log('[InactivityReport] Using cached data, age:', Math.round(ageHours * 10) / 10, 'hours');
                        
                        let customers = cacheData.inactiveCustomers;
                        
                        setInactiveCustomers(customers);
                        setUsingCache(true);
                        setCacheAge(Math.round(ageHours * 60)); // minutes
                        setLastCacheUpdate(lastUpdated);
                        setCacheAvailable(true);
                        setLoading(false);
                        return;
                    }
                }

                // No cache available - show message to admin to generate
                console.log('[InactivityReport] No cache available');
                setCacheAvailable(false);
                setLoading(false);

            } catch (error) {
                console.error('[InactivityReport] Error:', error);
                setToast({ message: `Error loading report: ${error.message}`, type: 'error' });
                setLoading(false);
            }
        };

        fetchData();
    }, [user, userRole, can]);

    // Generate cache via Cloud Function (admin only)
    const canRefreshCache = can(FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH);
    const handleGenerateCache = async () => {
        if (!canRefreshCache) {
            setToast({ message: 'Only admins can generate the cache', type: 'error' });
            return;
        }

        // Check if cache was updated within cooldown period
        if (lastCacheUpdate) {
            const hoursSinceUpdate = (Date.now() - lastCacheUpdate.getTime()) / (1000 * 60 * 60);
            if (hoursSinceUpdate < cacheRefreshCooldown) {
                const hoursRemaining = Math.ceil(cacheRefreshCooldown - hoursSinceUpdate);
                setToast({ 
                    message: `Cache was updated ${Math.round(hoursSinceUpdate * 10) / 10}h ago. Please wait ${hoursRemaining}h before refreshing again.`, 
                    type: 'error' 
                });
                return;
            }
        }

        const confirmed = window.confirm(
            'This will generate the customer inactivity report cache. ' +
            'It may take 1-2 minutes. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setGeneratingCache(true);
            setRefreshProgress(0);
            
            // Start progress timer (simulate progress)
            let progress = 0;
            refreshTimerRef.current = setInterval(() => {
                progress += 1;
                if (progress <= 90) { // Cap at 90% until actual completion
                    setRefreshProgress(progress);
                }
            }, 1000); // Update every second
            
            setToast({ message: 'Generating inactivity cache... This may take 1-2 minutes.', type: 'info' });
            
            console.log('[InactivityReport] Calling generateInactivityCache function...');
            const generateInactivityCacheFunc = httpsCallable(functions, 'generateInactivityCache');
            const result = await generateInactivityCacheFunc({});
            
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            setRefreshProgress(100);
            
            if (result.data?.success) {
                setToast({ message: `Cache generated: ${result.data.inactiveCount} inactive customers found`, type: 'success' });
                
                // Reload from cache after short delay
                setTimeout(async () => {
                    const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), CACHE_ID));
                    if (cacheDoc.exists()) {
                        const cacheData = cacheDoc.data();
                        let customers = cacheData.inactiveCustomers || [];
                        
                        setInactiveCustomers(customers);
                        setCacheAvailable(true);
                        setUsingCache(true);
                        setCacheAge(0);
                    }
                    setGeneratingCache(false);
                    setRefreshProgress(0);
                }, 1000);
            } else {
                throw new Error(result.data?.error || 'Unknown error');
            }
        } catch (error) {
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            console.error('[InactivityReport] Generate cache error:', error);
            setGeneratingCache(false);
            setRefreshProgress(0);
            
            let errorMessage = 'Failed to generate cache';
            if (error.code === 'unauthenticated') {
                errorMessage = 'Please sign in to generate the cache';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'Only admins can generate the cache';
            } else if (error.code === 'deadline-exceeded') {
                errorMessage = 'Request timed out. The data may be too large. Please try again.';
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    // Manual refresh (reload from cache)
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), CACHE_ID));
            if (cacheDoc.exists()) {
                const cacheData = cacheDoc.data();
                const lastUpdated = cacheData.lastUpdated?.toDate?.() || new Date(0);
                const ageHours = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);
                
                let customers = cacheData.inactiveCustomers || [];
                
                setInactiveCustomers(customers);
                setCacheAvailable(true);
                setUsingCache(true);
                setCacheAge(Math.round(ageHours * 60));
                setToast({ message: 'Report refreshed from cache', type: 'success' });
            } else {
                setCacheAvailable(false);
                setToast({ message: 'No cache available. Admin needs to generate it.', type: 'warning' });
            }
        } catch (error) {
            setToast({ message: `Refresh failed: ${error.message}`, type: 'error' });
        } finally {
            setRefreshing(false);
        }
    };

    // Get unique sales reps for filter
    const availableReps = useMemo(() => {
        const repSet = new Set();
        inactiveCustomers.forEach(c => {
            c.salesRepNames?.forEach(name => repSet.add(name));
        });
        return Array.from(repSet).sort();
    }, [inactiveCustomers]);

    // Get unique statuses for filter
    const availableStatuses = useMemo(() => {
        const statusSet = new Set();
        inactiveCustomers.forEach(c => {
            if (c.customerStatus) statusSet.add(c.customerStatus);
        });
        return Array.from(statusSet).sort();
    }, [inactiveCustomers]);

    // Get unique states for filter (normalized to uppercase to collapse case variants)
    const availableStates = useMemo(() => {
        const stateSet = new Set();
        inactiveCustomers.forEach(c => {
            if (c.customerState) stateSet.add(c.customerState.trim().toUpperCase());
        });
        return Array.from(stateSet).sort();
    }, [inactiveCustomers]);

    // Filtered and sorted data (uses applied/URL state, not draft)
    const { search, selectedRep, selectedStatus, selectedStates } = fb.applied;

    const filteredData = useMemo(() => {
        let data = [...inactiveCustomers];

        // Filter by sales rep
        if (selectedRep) {
            data = data.filter(c => c.salesRepNames?.includes(selectedRep));
        }

        // Filter by status
        if (selectedStatus) {
            data = data.filter(c => c.customerStatus === selectedStatus);
        }

        // Filter by state (case-insensitive, multi-select)
        if (selectedStates.length > 0) {
            const statesUpper = selectedStates.map(s => s.toUpperCase());
            data = data.filter(c => c.customerState && statesUpper.includes(c.customerState.trim().toUpperCase()));
        }

        // Search filter
        if (search.trim()) {
            const searchLower = search.toLowerCase().trim();
            data = data.filter(c =>
                c.customerName?.toLowerCase().includes(searchLower) ||
                c.customerEmail?.toLowerCase().includes(searchLower) ||
                c.customerAccountNumber?.toLowerCase().includes(searchLower) ||
                c.customerCity?.toLowerCase().includes(searchLower) ||
                c.customerState?.toLowerCase().includes(searchLower)
            );
        }

        // Sort
        data.sort((a, b) => {
            let aVal, bVal;

            // Handle special fields
            if (sortField === 'salesRepNames') {
                aVal = a.salesRepNames?.length ? a.salesRepNames.join(', ').toLowerCase() : '';
                bVal = b.salesRepNames?.length ? b.salesRepNames.join(', ').toLowerCase() : '';
            } else if (sortField === 'customerEmail') {
                aVal = (a.customerEmail || '').toLowerCase();
                bVal = (b.customerEmail || '').toLowerCase();
            } else {
                aVal = a[sortField];
                bVal = b[sortField];
            }

            // Handle null values for daysSinceOrder
            if (sortField === 'daysSinceOrder') {
                if (aVal === null && bVal === null) return 0;
                if (aVal === null) return sortDirection === 'desc' ? -1 : 1;
                if (bVal === null) return sortDirection === 'desc' ? 1 : -1;
            }

            // Handle string comparison
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            // Secondary sort: highest lifetime revenue first
            const aRev = Number(a.lifetimeRevenue) || 0;
            const bRev = Number(b.lifetimeRevenue) || 0;
            return bRev - aRev;
        });

        return data;
    }, [inactiveCustomers, selectedRep, selectedStatus, selectedStates, search, sortField, sortDirection]);

    // Pagination
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    useEffect(() => {
        const lastPage = Math.max(1, totalPages);
        if (currentPage > lastPage) setCurrentPage(lastPage);
    }, [currentPage, totalPages, setCurrentPage]);

    // Reset page when applied filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, selectedRep, selectedStatus, selectedStates, sortField, sortDirection, setCurrentPage]);

    // Sort handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Export to multiple formats
    const handleExport = (format) => {
        const headers = [
            'Account Number', 'Customer Name', 'Email', 'Phone', 'City', 'State',
            'Status', 'Business Type', 'Business Focus', 'Sales Reps',
            'Last Order Date', 'Days Since Order', 'Total Orders',
            'Lifetime Revenue', 'Inactivity Reason'
        ];
        
        const rowMapper = c => [
            c.customerAccountNumber,
            c.customerName,
            c.customerEmail,
            c.customerPhone,
            c.customerCity,
            c.customerState,
            c.customerStatus,
            c.businessType,
            c.businessFocus,
            c.salesRepNames?.join('; ') || '',
            c.lastOrderDate || 'Never',
            c.daysSinceOrder ?? 'Never',
            c.totalOrders,
            c.lifetimeRevenue.toFixed(2),
            c.inactivityReason === 'both' ? 'Status + No Orders'
                : c.inactivityReason === 'status-inactive' ? 'Status Inactive'
                : 'No Orders 365 Days'
        ];
        
        exportDataUtil({
            filename: `customer-inactivity-report-${new Date().toISOString().split('T')[0]}`,
            format,
            headers,
            data: filteredData,
            rowMapper,
            options: format.toLowerCase() === 'pdf' ? {
                title: `Customer Inactivity Report - ${new Date().toLocaleDateString()}`,
                fontSize: 7,
                orientation: 'landscape',
                pageSize: 'a4'
            } : {
                title: `Customer Inactivity Report - ${new Date().toLocaleDateString()}`,
                sheetName: 'Inactive Customers',
                columnWidths: [15, 25, 25, 15, 15, 10, 10, 15, 20, 25, 15, 12, 10, 12, 20]
            }
        });
        
        setToast({ message: `Exported ${filteredData.length} records to ${format.toUpperCase()}`, type: 'success' });
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Cache Generation Progress Overlay */}
            {generatingCache && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mb-4">
                                <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Generating Inactivity Cache</h3>
                            <p className="text-gray-600 mb-4">
                                Analyzing customer order history...
                            </p>
                            <div className="mb-2">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-1000" 
                                        style={{ width: `${refreshProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">
                                {refreshProgress < 30 
                                    ? `${refreshProgress}s - Fetching customer data...` 
                                    : refreshProgress < 60
                                    ? `${refreshProgress}s - Fetching order history...`
                                    : refreshProgress < 90
                                    ? `${refreshProgress}s - Calculating inactivity metrics...`
                                    : 'Finalizing cache generation...'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Customer Inactivity Report</h2>
                        <p className="text-gray-600">
                            Active customers with no orders in the past 365 days. Because the report only updates once a month, customers may have recent orders from this month and still appear here until the next cache refresh.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {usingCache && cacheAge !== null && (
                            <span className="text-xs text-gray-500">
                                Cached {cacheAge < 60 ? `${cacheAge}m` : `${Math.round(cacheAge / 60)}h`} ago
                            </span>
                        )}
                        {canRefreshCache && (
                            <Tooltip text="Regenerate cache from database (admin only)">
                                <button
                                    onClick={handleGenerateCache}
                                    disabled={generatingCache || refreshing}
                                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {generatingCache ? 'Generating...' : '⚙ Generate Cache'}
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>

            {/* No Cache Available Message */}
            {!cacheAvailable && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <svg className="w-8 h-8 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-semibold text-yellow-800">Report Cache Not Available</h3>
                            <p className="text-yellow-700 mt-1">
                                The inactivity report cache has not been generated yet. This report requires processing 
                                a large amount of data and must be computed server-side.
                            </p>
                            {canRefreshCache ? (
                                <p className="text-yellow-700 mt-2">
                                    Click the <strong>"⚙ Generate Cache"</strong> button above to generate the report. 
                                    This may take 1-2 minutes.
                                </p>
                            ) : (
                                <p className="text-yellow-700 mt-2">
                                    Please ask an administrator to generate the inactivity report cache.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters - only show when cache is available */}
            {cacheAvailable && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap gap-4 items-end">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            value={fb.draft.search}
                            onChange={(e) => fb.setDraft('search', e.target.value)}
                            onKeyDown={fb.applyOnEnter}
                            placeholder="Name, email, account #, city, state..."
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                            }`}
                        />
                    </div>

                    {/* Sales Rep Filter */}
                    {availableReps.length > 0 && (
                        <div className="min-w-[180px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sales Rep</label>
                            <select
                                value={fb.draft.selectedRep}
                                onChange={(e) => fb.setDraft('selectedRep', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All Reps</option>
                                {availableReps.map(rep => (
                                    <option key={rep} value={rep}>{rep}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Status Filter */}
                    {availableStatuses.length > 0 && (
                        <div className="min-w-[180px]">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Status</label>
                            <select
                                value={fb.draft.selectedStatus}
                                onChange={(e) => fb.setDraft('selectedStatus', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All Statuses</option>
                                {availableStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* State Filter (multi-select) */}
                    {availableStates.length > 0 && (
                        <div className="min-w-[150px] relative" ref={stateDropdownRef}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <button
                                type="button"
                                onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
                                className={`w-full px-3 py-2 border rounded-md text-left text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between ${
                                    fb.draft.selectedStates.length > 0 ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 bg-white'
                                }`}
                            >
                                <span className="truncate">
                                    {fb.draft.selectedStates.length === 0 ? 'All States' : fb.draft.selectedStates.length === 1 ? fb.draft.selectedStates[0] : `${fb.draft.selectedStates.length} selected`}
                                </span>
                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {stateDropdownOpen && (
                                <div className="absolute z-20 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {fb.draft.selectedStates.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => fb.setDraft('selectedStates', [])}
                                            className="w-full px-3 py-1.5 text-left text-xs text-indigo-600 hover:bg-gray-50 border-b border-gray-100"
                                        >
                                            Clear all
                                        </button>
                                    )}
                                    {availableStates.map(state => (
                                        <label key={state} className="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                                            <input
                                                type="checkbox"
                                                checked={fb.draft.selectedStates.includes(state)}
                                                onChange={() => {
                                                    const prev = fb.draft.selectedStates;
                                                    fb.setDraft('selectedStates',
                                                        prev.includes(state) ? prev.filter(s => s !== state) : [...prev, state]
                                                    );
                                                }}
                                                className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            {state}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Apply / Clear */}
                    <FilterActions
                        onApply={fb.apply}
                        onClear={fb.clear}
                        isDirty={fb.isDirty}
                        hasActiveFilters={fb.hasActiveFilters}
                    />

                    {/* Export */}
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={false}
                        tooltipText="Export filtered results in various formats"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                    />
                </div>

                {/* Results count */}
                <div className="mt-3 text-sm text-gray-600">
                    Showing {paginatedData.length} of {filteredData.length} inactive customers
                </div>
            </div>
            )}

            {/* Results Table - only show when cache is available */}
            {cacheAvailable && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerName')}
                                >
                                    Customer {sortField === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerStatus')}
                                >
                                    Status {sortField === 'customerStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('lastOrderDate')}
                                >
                                    Last Order {sortField === 'lastOrderDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('daysSinceOrder')}
                                >
                                    Days Inactive {sortField === 'daysSinceOrder' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerCity')}
                                >
                                    City {sortField === 'customerCity' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerState')}
                                >
                                    State {sortField === 'customerState' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('lifetimeRevenue')}
                                >
                                    Lifetime Revenue {sortField === 'lifetimeRevenue' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('salesRepNames')}
                                >
                                    Sales Rep {sortField === 'salesRepNames' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('customerEmail')}
                                >
                                    Contact {sortField === 'customerEmail' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedData.map((customer) => (
                                <tr key={customer.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <Link
                                            to={`/customer/${customer.id}`}
                                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                                        >
                                            {customer.customerName}
                                        </Link>
                                        <div className="text-xs text-gray-500">{customer.customerAccountNumber}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            customer.customerStatus === 'Inactive'
                                                ? 'bg-red-100 text-red-800'
                                                : customer.customerStatus === 'Active'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {customer.customerStatus || 'Unknown'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                        {customer.lastOrderDate ? formatDate(customer.lastOrderDate) : 'Never'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className={`text-sm font-medium ${
                                            customer.daysSinceOrder === null
                                                ? 'text-gray-600'
                                                : customer.daysSinceOrder > 730
                                                    ? 'text-red-600'
                                                    : customer.daysSinceOrder > 365
                                                        ? 'text-orange-600'
                                                        : 'text-yellow-600'
                                        }`}>
                                            {customer.daysSinceOrder === null 
                                                ? (customer.daysInSystem ? `${customer.daysInSystem} days (in system)` : 'Never ordered') 
                                                : `${customer.daysSinceOrder} days`}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                        {customer.customerCity || <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                        {customer.customerState || <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                        ${customer.lifetimeRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                        {customer.salesRepNames && customer.salesRepNames.length > 0 
                                            ? customer.salesRepNames.join(', ')
                                            : <span className="text-gray-400">Unassigned</span>}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                        <div>{customer.customerEmail}</div>
                                        <div>{customer.customerPhone}</div>
                                    </td>
                                </tr>
                            ))}
                            {paginatedData.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                        No inactive customers found matching your filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
            )}
        </main>
    );
};

export default CustomerInactivityReportPage;
